import React, { useState } from 'react';
import styled from 'styled-components';

// 스타일 컴포넌트
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  background-color: white;
  border-radius: 10px;
  width: 400px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  position: relative;
`;

const ModalHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid #ededed;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;
`;

const CloseIcon = styled.div`
  cursor: pointer;
  font-size: 20px;
  color: #999;
`;

const ModalBody = styled.div`
  padding: 20px;
`;

const InfoContainer = styled.div`
  background-color: #f8f9fa;
  border-radius: 5px;
  padding: 25px;
  margin-bottom: 20px;
`;

const InfoSection = styled.div`
  margin-bottom: 15px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoLabel = styled.div`
  font-size: 14px;
  color: #2282e9;
  margin-bottom: 8px;
  font-weight: 500;
`;

const InfoValue = styled.div`
  font-size: 16px;
  color: #333;
`;

const FileUploadSection = styled.div`
  margin-top: 25px;
  margin-bottom: 15px;
`;

const FileUploadLabel = styled.div`
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 12px;
`;

const FileUploadButton = styled.label`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 50px;
  border: 1px solid #ddd;
  border-radius: 5px;
  color: #2282e9;
  font-size: 14px;
  cursor: pointer;
  margin-bottom: 5px;
`;

const FileUploadInput = styled.input`
  display: none;
`;

const FileInfo = styled.div`
  font-size: 14px;
  color: #333;
  margin-top: 10px;
  margin-bottom: 5px;
`;

const FileUploadNote = styled.div`
  font-size: 12px;
  color: #999;
  text-align: left;
  margin-top: 5px;
`;

const SubmitButton = styled.button`
  width: 100%;
  height: 50px;
  border-radius: 5px;
  background-color: #dddddd;
  color: white;
  font-weight: 600;
  border: none;
  cursor: not-allowed;
  font-size: 16px;
  margin-top: 20px;
`;

// 에러 메시지를 위한 스타일 컴포넌트 추가
const ErrorMessage = styled.div`
  color: #ff6b6b;
  font-size: 14px;
  text-align: left;
`;

interface TaxInvoiceModalProps {
  onClose: () => void;
  onSubmit: (businessLicenseFile: File, businessCertFile: File) => Promise<void>;
  businessInfo: {
    name: string;
    email: string;
    phone: string;
  };
}

const TaxInvoiceModal: React.FC<TaxInvoiceModalProps> = ({
  onClose,
  onSubmit,
  businessInfo
}) => {
  const [businessLicenseFile, setBusinessLicenseFile] = useState<File | null>(null);
  const [businessCertFile, setBusinessCertFile] = useState<File | null>(null);
  // 에러 메시지 상태 추가
  const [errorMessage, setErrorMessage] = useState<string>("");

  const handleBusinessLicenseChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setBusinessLicenseFile(e.target.files[0]);
    }
  };

  const handleBusinessCertChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setBusinessCertFile(e.target.files[0]);
    }
  };

  const isSubmitEnabled = businessLicenseFile !== null && businessCertFile !== null;

  const handleSubmit = async () => {
    if (businessLicenseFile && businessCertFile) {
      try {
        setErrorMessage(""); // 제출 시도할 때 에러 메시지 초기화
        await onSubmit(businessLicenseFile, businessCertFile);
      } catch (error: any) {
        // HTTP 상태 코드에 따른 에러 메시지 설정
        if (error.response) {
          if (error.response.status === 400) {
            setErrorMessage("잘못된 요청입니다. 입력 내용을 확인해주세요.");
          } else if (error.response.status === 500) {
            setErrorMessage("서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.");
          } else {
            setErrorMessage("세금계산서 신청 중 오류가 발생했습니다.");
          }
        } else {
          setErrorMessage("네트워크 오류가 발생했습니다. 연결을 확인해주세요.");
        }
      }
    }
  };

  return (
    <ModalOverlay>
      <ModalContainer>
        <ModalHeader>
          <ModalTitle>세금계산서</ModalTitle>
          <CloseIcon onClick={onClose}>✕</CloseIcon>
        </ModalHeader>
        <ModalBody>
          <InfoContainer>
            <InfoSection>
              <InfoLabel>사업자 이름</InfoLabel>
              <InfoValue>{businessInfo.name}</InfoValue>
            </InfoSection>
            
            <InfoSection>
              <InfoLabel>이메일 주소</InfoLabel>
              <InfoValue>{businessInfo.email}</InfoValue>
            </InfoSection>
            
            <InfoSection>
              <InfoLabel>전화번호 번호</InfoLabel>
              <InfoValue>{businessInfo.phone}</InfoValue>
            </InfoSection>
          </InfoContainer>
          
          <FileUploadSection>
            <FileUploadLabel>통장사본 이미지</FileUploadLabel>
            {businessLicenseFile ? (
              <FileInfo>
                {businessLicenseFile.name}
              </FileInfo>
            ) : (
              <FileUploadButton htmlFor="businessLicense">
                + 파일 올리기
              </FileUploadButton>
            )}
            <FileUploadInput 
              id="businessLicense" 
              type="file" 
              accept=".pdf,.jpeg,.jpg,.png" 
              onChange={handleBusinessLicenseChange}
            />
            <FileUploadNote>10MB 이하PDF, JPEG, PNG 형식을 등록하세요.</FileUploadNote>
          </FileUploadSection>
          
          <FileUploadSection>
            <FileUploadLabel>사업자등록증</FileUploadLabel>
            {businessCertFile ? (
              <FileInfo>
                {businessCertFile.name}
              </FileInfo>
            ) : (
              <FileUploadButton htmlFor="businessCertificate">
                + 파일 올리기
              </FileUploadButton>
            )}
            <FileUploadInput 
              id="businessCertificate" 
              type="file" 
              accept=".pdf,.jpeg,.jpg,.png" 
              onChange={handleBusinessCertChange}
            />
            <FileUploadNote>10MB 이하PDF, JPEG, PNG 형식을 등록하세요.</FileUploadNote>
          </FileUploadSection>
          
          {/* 에러 메시지 표시 */}
          {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
          
          <SubmitButton 
            onClick={isSubmitEnabled ? handleSubmit : undefined}
            style={{
              backgroundColor: isSubmitEnabled ? '#2282e9' : '#dddddd',
              cursor: isSubmitEnabled ? 'pointer' : 'not-allowed'
            }}
          >
            신청하기
          </SubmitButton>
        </ModalBody>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default TaxInvoiceModal; 